import { useEffect, useMemo, useRef, useState } from "react";
import { ChangeAlertCallbackType } from "../../../Root";
import { IngressService } from "../../../../services/IngressService";
import { Button, Box } from "@mui/material";
import { useDialog } from "@albo-ui/core";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ModalStyle } from "../../../../util/Styles";
import { Tooltip, Switch } from "@mui/material";
import moment from "moment";
moment.locale("es");

export const useCompanyOnboardingTable = (changeAlert: ChangeAlertCallbackType, companyUpdate) => {
    const [data, setData] = useState([]);
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const ingressService = useRef<IngressService>();
    const [title, setTitle] = useState("");
    const [dialogText, setDialogText] = useState({});
    const [search, setSearch] = useState("");
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [statusSearch, setStatusSearch] = useState("enabled");
    const [filters, setFilters] = useState({status: statusSearch});
    
    const dialogProps = useDialog();
    const dialogConfirm = useDialog();

    const saveData = async (result) => {
        setLoading(true);
        setData(result.content);
        setTotalRows(result["pagination"] ? result["pagination"]["total_documents"] : 0);
        setLoading(false);
    };

    const getData = async (newPage, newPerPage=perPage, filters=null) => {
        setLoading(true);
        let request = {
            data: {
                page_number: newPage,
                results_per_page: newPerPage
            }
        }
        
        if(filters && Object.values(filters)[0]){
            request.data["filters"] = [filters]
        }
        
        const response = await ingressService.current.companyList(request);
        
        setPage(newPage)
        setData(response.data.widget[0].data.content || []);
        setTotalRows(response.data.widget[0].data.pagination?.total_documents || 0);
        setLoading(false);
    };

    const onOpenLinkAdd = (action, row? : any) => {
        row && setRow(row);
        action === "edit" ? setTitle("Editar empresa") : setTitle("Crear link de onboarding")
        dialogProps.openDialog();
    };

    const onOpenLinkDelete = (row : any) => {
        setRow(row);
        setDialogText({
            title: "Eliminar registro",
            message: "¿Está seguro que desea eliminar el siguiente registro?"
        })
        dialogConfirm.openDialog();
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        getData(newPage, perPage, filters ? filters : null);
    };
    const handlePerRowsChange = async (newPerPage, newPage) => {
        setPage(newPage);
        setPerPage(newPerPage);
        getData(newPage, newPerPage, filters ? filters : null);
    };

    const changeValueSearch = (type, e) => {
        let f = filters;
        f[type] = e.target.value.trimStart();
        setSearch(e.target.value.trimStart());
        type === "company_id" ? delete f["corporate_name"] : delete f["company_id"];
        setFilters(f);
    };

    const companySearch = () => {
        getData(1, perPage, filters);
    };

    const filterStatus = () => {
        let f = { status: "" };
        if (statusSearch === "enabled") {
            setStatusSearch("disabled");
            f["status"] = "disabled";
        } else {
            setStatusSearch("enabled");
            f["status"] = "enabled";
        }
        setFilters(f);
        getData(1, perPage, f);
    };

    const activateLink = (uuid) => {
        let form = { 
            company: {
                status: "enabled",
                uuid: uuid
            }
        };
        companyUpdate(form);
    };

    const reset = () => {
        setSearch("");
        setFilters({ status: statusSearch });
        setPage(1);
        getData(1, perPage, { status: statusSearch });
    };

    useEffect(() => {
        setSearch("");
        console.log(search);

        ingressService.current = new IngressService(changeAlert);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const calculateColumns = useMemo(() => {
        const columns = [
            { selector: (row) => row["company_id"], name: "Id", wrap: true, sortable: true, width: "10%" },
            { selector: (row) => row["corporate_name"], name: "Nombre de Empresa", wrap: true, sortable: true, width: "18%" },
            { selector: (row) => row["onboarding_url"], name: "Link de Onboarding", wrap: true, sortable: true, width: "29%" },
            { selector: (row) => moment(row.udate).format("LL"), name: "Fecha actualización", wrap: true, sortable: true, width: "14%" },
            { cell: (row, index) => (
                    <Box src={row["logo_url"]} component="img" alt={"image-" + index} sx={{ maxHeight: 45, py: 0.1 }}/>
                ), name: "Logo", wrap: true, sortable: true, center: true
            },
            { cell: (row) => {
                    return statusSearch !== "disabled" ?
                    <ModalStyle property={String(false)}>
                        <Button onClick={onOpenLinkAdd.bind(this,"edit", row)} sx={{ minWidth: "40px", p: 0.7, mr: 2 }} variant="contained" color="secondary">
                            <EditIcon />
                        </Button>
                        <Button onClick={onOpenLinkDelete.bind(this, row)} sx={{ minWidth: "40px", p: 0.7}} variant="contained" color="secondary">
                            <DeleteIcon />
                        </Button>
                    </ModalStyle>
                    :
                    <ModalStyle property={String(statusSearch === "disabled" ? true : false)}>
                        <Tooltip title={"Reactivar Link"}>
                            <Switch onClick={activateLink.bind(this, row.uuid)} checked={false}/>
                        </Tooltip>
                    </ModalStyle>

                }, name: "Acciones", wrap: true, sortable: true, center: true
            },
        ];

        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusSearch]);

    return {
        columns: calculateColumns,
        row,
        data,
        title,
        loading,
        dialogText,
        dialogProps,
        dialogConfirm,
        filters, 
        page, 
        perPage, 
        totalRows,
        statusSearch,
        saveData,
        getData,
        setRow,
        onOpenLinkAdd,
        reset, 
        companySearch, 
        changeValueSearch,
        handlePageChange, 
        handlePerRowsChange,
        filterStatus
    };
};
